// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CartsDataViewContext } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import updateSearchFields from "@insite/client-framework/Store/Pages/SavedOrderList/Handlers/UpdateSearchFields";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Pagination, { PaginationPresentationProps } from "@conklin/MobiusOverrides/Pagination/Pagination";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapDispatchToProps = {
    updateSearchFields,
};

// XNG-Changes: Add new Fields
const enum fields {
    showResultsPerPage = "showResultsPerPage",
    showPagesNumber = "showPagesNumber",
}

// XNG-Changes: Add new Fields
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showResultsPerPage]: boolean;
        [fields.showPagesNumber]: boolean;
    };
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

export interface SavedOrderListPaginationStyles {
    pagination?: PaginationPresentationProps;
}

export const savedOrderListPaginationStyles: SavedOrderListPaginationStyles = {};

const styles = savedOrderListPaginationStyles;

const SavedOrderListPagination = ({ updateSearchFields, fields: { showResultsPerPage, showPagesNumber } }: Props) => {
    const savedOrderListDataView = useContext(CartsDataViewContext);

    const changePage = (newPageIndex: number) => {
        updateSearchFields({
            page: newPageIndex,
        });
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        updateSearchFields({
            page: 1,
            pageSize: newPageSize,
        });
    };

    if (!savedOrderListDataView.value) {
        return null;
    }

    const { pagination } = savedOrderListDataView;
    if (!pagination || pagination.totalItemCount === 0) {
        return null;
    }

    return (
        <Pagination
            {...styles.pagination}
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
            prevPage={pagination.prevPageUri}
            nextPage={pagination.nextPageUri}
            // XNG-Changes
            showResultsPerPage={showResultsPerPage}
            showPagesNumber={showPagesNumber}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(null, mapDispatchToProps)(SavedOrderListPagination),
    definition: {
        group: "Saved Order List",
        displayName: "Pagination",
        allowedContexts: ["SavedOrderListPage"],
        fieldDefinitions: [
            {
                name: fields.showResultsPerPage,
                displayName: "Show Results Per Page",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showPagesNumber,
                displayName: "Show Pages Number",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
